import { RefObject, useEffect, useState } from 'react';

const SPINNER_HALF = 75; // half of the 150px spinner
const HEADER_GAP = 150; // min gap between the form header and the spinner top edge

type FormLoadingPosition = {
  spinnerPos: { left: number; top: number } | null;
};

// Tracks the viewport-px position of the submit loading spinner that is
// portaled to document.body. Horizontally centered on .form-content;
// vertically centered in the viewport but clamped so its top edge never rises
// closer than HEADER_GAP below the form header. Recomputes while submitting and
// on window resize.
export const useFormLoadingPosition = (
  isSubmitting: boolean,
  formContentRef: RefObject<HTMLDivElement | null>
): FormLoadingPosition => {
  const [spinnerPos, setSpinnerPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  useEffect(() => {
    if (!isSubmitting) return;

    const updatePos = () => {
      const el = formContentRef.current;

      if (!el) return;

      const rect = el.getBoundingClientRect();
      // .form-content top ≈ form header bottom
      const minTop = rect.top + HEADER_GAP + SPINNER_HALF;

      setSpinnerPos({
        left: rect.left + rect.width / 2,
        top: Math.max(window.innerHeight / 2, minTop),
      });
    };

    updatePos();
    window.addEventListener('resize', updatePos);

    return () => window.removeEventListener('resize', updatePos);
  }, [isSubmitting]);

  return { spinnerPos };
};
